// Hotwire pusher streaming setup
// Handles both existing hotwire:inject and new pusher_refresh events
import utils from 'utils/MiscUtils'

// Check if user is actively editing within the target frame area
function isEditingInFrame(targetFrameId) {
  const targetFrame = document.getElementById(targetFrameId)
  if (!targetFrame)
    return false

  // Check for jQuery UI drag operations within the target frame
  const isDragging = targetFrame.querySelector('.ui-sortable-helper, .ui-draggable-dragging')
                    || targetFrame.querySelector('[data-dragging="true"]')

  // Check if active element is within the target frame and is editable
  const activeElement = document.activeElement
  const isActiveElementEditing = activeElement && targetFrame.contains(activeElement) && (
    activeElement.tagName === 'INPUT'
    || activeElement.tagName === 'TEXTAREA'
    || activeElement.tagName === 'SELECT'
    || activeElement.contentEditable === 'true'
  )

  // Check for open dropdowns or modals (still global since they could be project-related)
  const hasOpenDropdown = document.querySelector('.dropdown-menu.show, .modal.show')

  return !!(isDragging || isActiveElementEditing || hasOpenDropdown)
}

// Cooldown handler for debouncing updates
function createCooldownHandler(cooldownMs = 3000) {
  let cooldownPromise = Promise.resolve()
  let pendingHandler = null
  let isScheduled = false

  return (eventHandler) => {
    // Always store the latest event handler
    pendingHandler = eventHandler

    // Only schedule execution if nothing is already scheduled
    if (!isScheduled) {
      isScheduled = true

      cooldownPromise = cooldownPromise.then(async () => {
        const handlerToExecute = pendingHandler
        pendingHandler = null
        isScheduled = false

        if (handlerToExecute) {
          handlerToExecute()
          await new Promise((resolve) => {
            setTimeout(resolve, cooldownMs)
          })
        }
      })
    }
  }
}

function setupPusherStreams(gfApp) {
  if (!gfApp || !gfApp.current_organization_id) {
    utils.warn('Cannot setup pusher streams without gfApp and organization ID')
    return
  }

  const orgChannel = gfApp.comm.subscribe({name: 'organization', id: gfApp.current_organization_id})

  // Create cooldown handler for turbo:refresh events
  const handleRefreshWithCooldown = createCooldownHandler(3000)

  // Existing hotwire:inject handling (moved from GlassFrogApp.js)
  orgChannel.bind('hotwire:inject', (payload) => {
    document.body?.insertAdjacentHTML('beforeend', payload)
  })

  // Real-time morphing refresh with React lifecycle and edit state checking
  orgChannel.bind('gf:turboRefresh', (data) => {
    utils.info('Received gf:turboRefresh event for:', data.target)
    handleRefreshWithCooldown(() => {
      // Skip update if user is actively editing/dragging within the target frame
      if (isEditingInFrame(data.target)) {
        utils.info('User is editing within target frame, skipping refresh for:', data.target)
        return
      }

      performMorphingRefresh(data)
    })
  })
}

function performMorphingRefresh(data) {
  const targetFrame = document.getElementById(data.target)
  if (!targetFrame) {
    utils.warn('Target frame not found for morphing refresh:', data.target)
    return
  }

  // Use frame src if available, otherwise fall back to data-refresh-url
  const refreshUrl = targetFrame.src || targetFrame.dataset.refreshUrl
  if (!refreshUrl) {
    utils.warn('No refresh URL available for frame:', data.target)
    return
  }

  utils.info('Morphing refresh for:', data.target)

  fetch(refreshUrl)
    .then((response) => response.text())
    .then((html) => {
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const fetchedFrame = doc.querySelector(`#${data.target}`)

      if (fetchedFrame && window.Idiomorph) {
        // Fast React cycle with pre-fetched content
        ReactRailsUJS.unmountComponents(`#${data.target}`)

        window.Idiomorph.morph(targetFrame, fetchedFrame, {
          ignoreActive: true,
          ignoreActiveValue: true,
        })

        // Store the refresh URL as a data attribute for future refreshes
        targetFrame.dataset.refreshUrl = refreshUrl

        ReactRailsUJS.mountComponents(`#${data.target}`)
      }
    })
    .catch((error) => {
      utils.warn('Morphing refresh failed:', error)
      if (targetFrame.reload)
        targetFrame.reload()
    })
}

export default {setupPusherStreams}
